/**
 * @file transfer-rows.ts
 * @description Правила строк документа «Перемещение» на стороне админки
 * (phase19.23 этап B, inventory.md W15).
 *
 * Зеркало серверного `transfer-rules.ts` в той части, которая нужна UI до
 * запроса: «строка не готова» (нет «Куда»), «строка внутрискладская» (кол-во
 * read-only, переносится вся партия) и «в документе есть межсклад» (печать
 * накладной). Чистые функции вынесены из компонентов, чтобы проверяться
 * юнит-тестом, а не глазами на 360px.
 */

/** Минимум полей строки, нужный правилам. */
export interface TransferRowLike {
  toWarehouseId: string;
  toBinId: string | null;
}

/** Перестановка внутри склада-источника: адрес меняется, остаток — нет. */
export function isIntraWarehouseRow(
  row: TransferRowLike,
  fromWarehouseId: string,
): boolean {
  return row.toWarehouseId === fromWarehouseId;
}

/**
 * Строка без «Куда»: внутри склада без ячейки переезжать некуда.
 * Межскладская строка адреса не требует — склад назначения уже задан.
 */
export function isRowTargetMissing(
  row: TransferRowLike,
  fromWarehouseId: string,
): boolean {
  return isIntraWarehouseRow(row, fromWarehouseId) && row.toBinId === null;
}

/**
 * Номера строк без «Куда» (1-based) — подпись под заблокированной кнопкой
 * проведения: «Заполните «Куда» в строках: 3, 5».
 */
export function missingTargetRowNumbers(
  rows: readonly TransferRowLike[],
  fromWarehouseId: string,
): number[] {
  return rows.reduce<number[]>((acc, row, index) => {
    if (isRowTargetMissing(row, fromWarehouseId)) acc.push(index + 1);
    return acc;
  }, []);
}

/**
 * Есть ли межскладская строка. От этого зависят две вещи: право
 * `SHOP_WMS_MANAGE` (сервер) и кнопка печати накладной (её прячем, иначе
 * сервер ответит `transfer_internal_only`).
 */
export function hasInterWarehouseRow(
  rows: readonly TransferRowLike[],
  fromWarehouseId: string,
): boolean {
  return rows.some((row) => !isIntraWarehouseRow(row, fromWarehouseId));
}
